import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Hash,
  Headphones,
  Loader2,
  Music,
  Phone,
  RefreshCw,
  Rocket,
  Save,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const steps = [
  { id: "overview", label: "Resumo", icon: ClipboardCheck },
  { id: "extensions", label: "Ramais", icon: Phone },
  { id: "queue", label: "Fila", icon: Headphones },
  { id: "ivr", label: "URA", icon: Workflow },
  { id: "finish", label: "Revisao", icon: ShieldCheck },
];

const emptyInventory = {
  ramais: [],
  dids: [],
  filas: [],
  ivrs: [],
  moh: [],
  anuncios: [],
  rotas: [],
  operadoras: [],
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function StepButton({ step, active, done, onClick }) {
  const Icon = step.icon;
  return (
    <button
      type="button"
      className={`flex min-w-[132px] items-center gap-2 rounded-sm border px-3 py-2 text-left text-sm ${
        active ? "border-[color:var(--accent-blue)] bg-[rgba(37,99,235,0.08)]" : "border-[color:var(--border-default)] bg-white"
      }`}
      onClick={onClick}
    >
      <span className={`flex h-7 w-7 items-center justify-center rounded-sm ${done ? "bg-[rgba(5,150,105,0.14)] text-[color:var(--accent-green)]" : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"}`}>
        {done ? <CheckCircle2 size={15} /> : <Icon size={15} />}
      </span>
      <span>{step.label}</span>
    </button>
  );
}

function Metric({ label, value, detail, icon: Icon }) {
  return (
    <div className="sbc-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
          <div className="mt-2 text-2xl font-mono font-semibold">{value}</div>
          {detail && <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">{detail}</div>}
        </div>
        <Icon size={18} className="text-[color:var(--text-secondary)]" />
      </div>
    </div>
  );
}

function ChecklistItem({ ok, title, detail, to }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--border-default)] px-4 py-3 last:border-b-0">
      <div className="flex min-w-0 items-center gap-3">
        <span className={`sbc-tag ${ok ? "tag-online" : "tag-warning"}`}>{ok ? "OK" : "Pendente"}</span>
        <div className="min-w-0">
          <div className="font-medium">{title}</div>
          <div className="text-xs text-[color:var(--text-muted)]">{detail}</div>
        </div>
      </div>
      {to && (
        <Link className="sbc-btn" to={to}>
          Abrir <ArrowRight size={13} />
        </Link>
      )}
    </div>
  );
}

export default function SetupWizard() {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState("overview");
  const [inventory, setInventory] = useState(emptyInventory);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [extensionForm, setExtensionForm] = useState({
    numero: "200",
    numero_fim: "209",
    nome: "Ramal",
    senha: "",
    caller_id_externo: "",
    codec: "PCMA,PCMU,G729",
    transporte: "udp",
    recording_enabled: true,
    enabled: true,
  });

  const [queueForm, setQueueForm] = useState({
    numero: "700",
    nome: "Atendimento",
    estrategia: "longest-idle",
    timeout: 360,
    max_wait_time: 360,
    wrap_up_time: 10,
    musica: "local_stream://moh",
    fallback: "",
    enabled: true,
  });

  const [ivrForm, setIvrForm] = useState({
    numero: "800",
    nome: "Atendimento principal",
    mensagem: "Obrigado por ligar. Digite 1 para atendimento.",
    destino_opcao_1: "700",
    timeout_destino: "700",
    invalid_destino: "700",
    tentativas: 3,
    enabled: true,
  });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [ramais, dids, filas, ivrs, moh, anuncios, rotas, operadoras] = await Promise.all([
        api.get("/ramais").then(({ data }) => data).catch(() => []),
        api.get("/dids").then(({ data }) => data).catch(() => []),
        api.get("/filas").then(({ data }) => data).catch(() => []),
        api.get("/ivrs").then(({ data }) => data).catch(() => []),
        api.get("/moh").then(({ data }) => data).catch(() => []),
        api.get("/anuncios").then(({ data }) => data).catch(() => []),
        api.get("/rotas").then(({ data }) => data).catch(() => []),
        api.get("/operadoras").then(({ data }) => data).catch(() => []),
      ]);
      setInventory({
        ramais: safeArray(ramais),
        dids: safeArray(dids),
        filas: safeArray(filas),
        ivrs: safeArray(ivrs),
        moh: safeArray(moh),
        anuncios: safeArray(anuncios),
        rotas: safeArray(rotas),
        operadoras: safeArray(operadoras),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const done = useMemo(() => ({
    overview: true,
    extensions: inventory.ramais.length > 0,
    queue: inventory.filas.length > 0,
    ivr: inventory.ivrs.length > 0,
    finish: inventory.ramais.length > 0 && (inventory.filas.length > 0 || inventory.ivrs.length > 0),
  }), [inventory]);

  const firstQueueNumber = inventory.filas[0]?.numero || queueForm.numero || "700";
  const tenantName = user?.tenant?.nome || user?.tenant_name || user?.tenant_id || "cliente";

  const markPending = () => window.dispatchEvent(new Event("voxyra-ipbx-config-pending"));

  const saveExtensions = async (event) => {
    event.preventDefault();
    setSaving("extensions");
    setError("");
    setMessage("");
    try {
      await api.post("/ramais", {
        ...extensionForm,
        batch_enabled: !!extensionForm.numero_fim,
        nome: extensionForm.nome || "Ramal",
        senha: extensionForm.senha || "",
        caller_id_interno: extensionForm.numero,
      });
      setMessage("Ramais criados com sucesso.");
      markPending();
      await load();
      setActiveStep("queue");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving("");
    }
  };

  const saveQueue = async (event) => {
    event.preventDefault();
    setSaving("queue");
    setError("");
    setMessage("");
    try {
      await api.post("/filas", {
        ...queueForm,
        modo_atendimento: "agentes",
        ramais: [],
        timeout: Math.max(Number(queueForm.timeout || 360), 360),
        max_wait_time: Math.max(Number(queueForm.max_wait_time || 360), 360),
        wrap_up_time: Number(queueForm.wrap_up_time || 10),
        service_level_seconds: 20,
        service_level_target: 80,
        discard_abandoned_after: 60,
      });
      setMessage("Fila inicial criada com sucesso.");
      markPending();
      await load();
      setActiveStep("ivr");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving("");
    }
  };

  const saveIvr = async (event) => {
    event.preventDefault();
    setSaving("ivr");
    setError("");
    setMessage("");
    try {
      await api.post("/ivrs", {
        numero: ivrForm.numero,
        nome: ivrForm.nome,
        mensagem: ivrForm.mensagem,
        opcoes: ivrForm.destino_opcao_1 ? { "1": ivrForm.destino_opcao_1 } : {},
        timeout_destino: ivrForm.timeout_destino || firstQueueNumber,
        invalid_destino: ivrForm.invalid_destino || firstQueueNumber,
        tentativas: Number(ivrForm.tentativas || 3),
        enabled: !!ivrForm.enabled,
      });
      setMessage("URA inicial criada com sucesso.");
      markPending();
      await load();
      setActiveStep("finish");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving("");
    }
  };

  const section = () => {
    if (activeStep === "extensions") {
      return (
        <form className="sbc-card p-5 space-y-4" onSubmit={saveExtensions}>
          <div>
            <h2 className="font-semibold">Criar primeiros ramais</h2>
            <p className="mt-1 text-sm text-[color:var(--text-secondary)]">Cria uma faixa inicial para os telefones do cliente.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ramal inicial</span>
              <input className="sbc-input font-mono" value={extensionForm.numero} onChange={(e) => setExtensionForm({ ...extensionForm, numero: e.target.value })} required />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ramal final</span>
              <input className="sbc-input font-mono" value={extensionForm.numero_fim} onChange={(e) => setExtensionForm({ ...extensionForm, numero_fim: e.target.value })} placeholder="opcional" />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Nome base</span>
              <input className="sbc-input" value={extensionForm.nome} onChange={(e) => setExtensionForm({ ...extensionForm, nome: e.target.value })} required />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Senha SIP</span>
              <input className="sbc-input font-mono" value={extensionForm.senha} onChange={(e) => setExtensionForm({ ...extensionForm, senha: e.target.value })} placeholder="automatica pelo codigo do cliente" />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">CID externo</span>
              <input className="sbc-input font-mono" value={extensionForm.caller_id_externo} onChange={(e) => setExtensionForm({ ...extensionForm, caller_id_externo: e.target.value })} placeholder="5571..." />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Transporte</span>
              <select className="sbc-input" value={extensionForm.transporte} onChange={(e) => setExtensionForm({ ...extensionForm, transporte: e.target.value })}>
                <option value="udp">UDP</option>
                <option value="tcp">TCP</option>
                <option value="tls">TLS</option>
              </select>
            </label>
          </div>
          <div className="flex justify-end">
            <button className="sbc-btn sbc-btn-primary" disabled={saving === "extensions"}>
              {saving === "extensions" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Criar ramais
            </button>
          </div>
        </form>
      );
    }

    if (activeStep === "queue") {
      return (
        <form className="sbc-card p-5 space-y-4" onSubmit={saveQueue}>
          <div>
            <h2 className="font-semibold">Criar fila inicial</h2>
            <p className="mt-1 text-sm text-[color:var(--text-secondary)]">A fila nasce por agentes do call center e pode ser ajustada depois no menu de filas.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Numero da fila</span>
              <input className="sbc-input font-mono" value={queueForm.numero} onChange={(e) => setQueueForm({ ...queueForm, numero: e.target.value })} required />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Nome</span>
              <input className="sbc-input" value={queueForm.nome} onChange={(e) => setQueueForm({ ...queueForm, nome: e.target.value })} required />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Estrategia</span>
              <select className="sbc-input" value={queueForm.estrategia} onChange={(e) => setQueueForm({ ...queueForm, estrategia: e.target.value })}>
                <option value="longest-idle">Mais ocioso</option>
                <option value="ring-all">Todos</option>
                <option value="round-robin">Round robin</option>
                <option value="top-down">Ordem dos agentes</option>
              </select>
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Sem agente</span>
              <input className="sbc-input font-mono" type="number" value={queueForm.timeout} onChange={(e) => setQueueForm({ ...queueForm, timeout: e.target.value })} />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Espera maxima</span>
              <input className="sbc-input font-mono" type="number" value={queueForm.max_wait_time} onChange={(e) => setQueueForm({ ...queueForm, max_wait_time: e.target.value })} />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Wrap-up</span>
              <input className="sbc-input font-mono" type="number" value={queueForm.wrap_up_time} onChange={(e) => setQueueForm({ ...queueForm, wrap_up_time: e.target.value })} />
            </label>
          </div>
          <div className="flex justify-end">
            <button className="sbc-btn sbc-btn-primary" disabled={saving === "queue"}>
              {saving === "queue" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Criar fila
            </button>
          </div>
        </form>
      );
    }

    if (activeStep === "ivr") {
      return (
        <form className="sbc-card p-5 space-y-4" onSubmit={saveIvr}>
          <div>
            <h2 className="font-semibold">Criar URA principal</h2>
            <p className="mt-1 text-sm text-[color:var(--text-secondary)]">Cria uma URA simples com opcao 1 apontando para a fila inicial.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Numero da URA</span>
              <input className="sbc-input font-mono" value={ivrForm.numero} onChange={(e) => setIvrForm({ ...ivrForm, numero: e.target.value })} required />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Nome</span>
              <input className="sbc-input" value={ivrForm.nome} onChange={(e) => setIvrForm({ ...ivrForm, nome: e.target.value })} required />
            </label>
            <label className="md:col-span-2">
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Mensagem</span>
              <textarea className="sbc-input min-h-24" value={ivrForm.mensagem} onChange={(e) => setIvrForm({ ...ivrForm, mensagem: e.target.value })} />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Opcao 1</span>
              <input className="sbc-input font-mono" value={ivrForm.destino_opcao_1} onChange={(e) => setIvrForm({ ...ivrForm, destino_opcao_1: e.target.value })} placeholder={firstQueueNumber} />
            </label>
            <label>
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Timeout / invalido</span>
              <input className="sbc-input font-mono" value={ivrForm.timeout_destino} onChange={(e) => setIvrForm({ ...ivrForm, timeout_destino: e.target.value, invalid_destino: e.target.value })} placeholder={firstQueueNumber} />
            </label>
          </div>
          <div className="flex justify-end">
            <button className="sbc-btn sbc-btn-primary" disabled={saving === "ivr"}>
              {saving === "ivr" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Criar URA
            </button>
          </div>
        </form>
      );
    }

    return (
      <div className="sbc-card overflow-hidden">
        <ChecklistItem ok={inventory.operadoras.length > 0 || inventory.rotas.length > 0} title="Saida por tronco e rotas" detail={`${inventory.operadoras.length} tronco(s), ${inventory.rotas.length} rota(s)`} to="/lcr/rotas-saida" />
        <ChecklistItem ok={inventory.dids.length > 0} title="DID de entrada" detail={`${inventory.dids.length} DID(s) cadastrado(s)`} to="/troncos/dids" />
        <ChecklistItem ok={inventory.ramais.length > 0} title="Ramais" detail={`${inventory.ramais.length} ramal(is) cadastrado(s)`} to="/ramais/cadastro" />
        <ChecklistItem ok={inventory.filas.length > 0} title="Fila inicial" detail={`${inventory.filas.length} fila(s) cadastrada(s)`} to="/call-center/filas" />
        <ChecklistItem ok={inventory.ivrs.length > 0} title="URA principal" detail={`${inventory.ivrs.length} URA(s) cadastrada(s)`} to="/troncos/ura" />
        <ChecklistItem ok={inventory.moh.length > 0 || inventory.anuncios.length > 0} title="Audios e musica" detail={`${inventory.moh.length} musica(s), ${inventory.anuncios.length} anuncio(s)`} to="/sistema/gerador-audios" />
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="setup-wizard-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Implantacao guiada</div>
          <h1 className="text-2xl font-semibold tracking-tight">Assistente de configuracao</h1>
          <p className="mt-1 text-sm text-[color:var(--text-secondary)]">{tenantName}</p>
        </div>
        <button className="sbc-btn" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Metric icon={Phone} label="Ramais" value={inventory.ramais.length} detail="cadastro inicial" />
        <Metric icon={Headphones} label="Filas" value={inventory.filas.length} detail="atendimento" />
        <Metric icon={Workflow} label="URAs" value={inventory.ivrs.length} detail="entrada" />
        <Metric icon={Hash} label="DIDs" value={inventory.dids.length} detail="numeros recebidos" />
      </div>

      <div className="sbc-card p-3">
        <div className="flex gap-2 overflow-x-auto">
          {steps.map((step) => (
            <StepButton
              key={step.id}
              step={step}
              active={activeStep === step.id}
              done={!!done[step.id]}
              onClick={() => setActiveStep(step.id)}
            />
          ))}
        </div>
      </div>

      {message && <div className="rounded-sm border border-[rgba(5,150,105,0.3)] bg-[rgba(5,150,105,0.08)] px-3 py-2 text-xs text-[color:var(--accent-green)]">{message}</div>}
      {error && <div className="rounded-sm border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 text-xs font-mono text-[color:var(--accent-red)]">! {error}</div>}

      {activeStep === "overview" ? (
        <div className="sbc-card p-5">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] text-[color:var(--accent-blue)]">
              <Rocket size={20} />
            </div>
            <div>
              <h2 className="font-semibold">Comece pela configuracao minima</h2>
              <p className="mt-1 text-sm text-[color:var(--text-secondary)]">
                O fluxo cria a base operacional do cliente e deixa as configuracoes sensiveis, como DID e tronco, com atalhos para revisao.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button className="sbc-btn sbc-btn-primary" onClick={() => setActiveStep("extensions")}>
                  Iniciar <ArrowRight size={14} />
                </button>
                <Link className="sbc-btn" to="/sistema/diagnostico">
                  Diagnostico do cliente
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : section()}

      <div className="sbc-card p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <Music size={15} />
          Proximos ajustes recomendados
        </div>
        <div className="grid gap-2 md:grid-cols-3">
          <Link className="sbc-btn justify-center" to="/troncos/dids">Apontar DID para URA</Link>
          <Link className="sbc-btn justify-center" to="/call-center/agentes">Cadastrar agentes</Link>
          <Link className="sbc-btn justify-center" to="/sistema/gerador-audios">Criar audios</Link>
        </div>
      </div>
    </div>
  );
}
